import { HD_BODYGRAPH_CHANNELS } from './hd-bodygraph-metadata';
import { HdBodygraphChannelState } from './hd-bodygraph.models';

/**
 * Resultaat van een kanaal-highlight resolutie voor één poort:
 *  - `channelKeys`: alle defined kanalen die de poort bevatten, in canonieke
 *    `HD_BODYGRAPH_CHANNELS`-volgorde;
 *  - `gates`: de gededupliceerde unie van al hun poorten (highlight-set).
 * Zie spec `2026-07-02-hd-kanaal-highlight-en-kanaal-info-design.md` §3.1.
 */
export interface HdChannelHighlightResolution {
  readonly channelKeys: readonly string[];
  readonly gates: readonly number[];
}

/**
 * Alle *defined* kanalen die de gegeven poort bevatten, in canonieke volgorde.
 * Bij integratiepoorten (10/20/34/57) kunnen dit er meerdere zijn; bewust
 * NIET transitief (kanalen zonder deze poort blijven buiten beschouwing).
 */
export function resolveDefinedHdChannelsForGate(
  channelStates: Readonly<Record<string, HdBodygraphChannelState>>,
  gate: number,
): HdBodygraphChannelState[] {
  const result: HdBodygraphChannelState[] = [];
  for (const definition of HD_BODYGRAPH_CHANNELS) {
    const state = channelStates[definition.key];
    if (state?.isDefined && state.gates.includes(gate)) {
      result.push(state);
    }
  }
  return result;
}

/**
 * Kanaal-highlight voor een kanaalstuk-hover/klik op `gate`, of `null` wanneer
 * de poort in geen enkel defined kanaal zit — de caller valt dan terug op het
 * bestaande éénpoorts-gedrag.
 */
export function resolveHdChannelHighlightForGate(
  channelStates: Readonly<Record<string, HdBodygraphChannelState>>,
  gate: number,
): HdChannelHighlightResolution | null {
  const channels = resolveDefinedHdChannelsForGate(channelStates, gate);
  if (channels.length === 0) {
    return null;
  }
  const gates: number[] = [];
  for (const channel of channels) {
    for (const channelGate of channel.gates) {
      if (!gates.includes(channelGate)) {
        gates.push(channelGate);
      }
    }
  }
  return { channelKeys: channels.map(channel => channel.key), gates };
}
